import { add, dist, dot, norm, scale, sub } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, hv, MoveKind, setHv } from "../../sim/components.ts";
import { addOverhealth, aim, applyDamage, applyStatus, damageBarrier, isEnemy, moveProjectile, playersInRadius, spawnProjectile, spawnZone } from "../../sim/combat.ts";
import { constructsInRadius, damageConstruct, nearestEnemy, rayConstructs, spawnConstruct } from "../../sim/constructs.ts";
import { targetPoint } from "../../sim/targeting.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { ck, fxk, pk, zk, type HeroModule } from "../module.ts";
import { action, commonInput, consume, glide, knockUp, launchProjectile, ownerAlive, ready, secs, startMove, toGround, trigger, useUlt } from "../kitlib.ts";

export const V = { leap: 0 } as const;
export const PK = { Dart: pk(HeroId.NoonKnight, 0), Blade: pk(HeroId.NoonKnight, 1), AnkhShot: pk(HeroId.NoonKnight, 2) } as const;
export const ZK = { Talons: zk(HeroId.NoonKnight, 0) } as const;
export const CK = { Ankh: ck(HeroId.NoonKnight, 0) } as const;
export const FX = { Dart: fxk(HeroId.NoonKnight, 0), Moon: fxk(HeroId.NoonKnight, 1), Talon: fxk(HeroId.NoonKnight, 2) } as const;

const def: HeroDef = {
  id: HeroId.NoonKnight,
  slug: "noon-knight",
  name: "Noon Knight",
  role: Role.Damage,
  color: "#e8e8f0",
  blurb: "Ricochet duelist who pins sun-ankhs, grapples to angles, glides, and calls a falling talon storm.",
  health: 275,
  armor: 0,
  shields: 0,
  radius: 0.4,
  height: 1.9,
  headRadius: 0.23,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 30,
  reloadTime: 1.6,
  ultCost: 3100,
  abilities: {
    [Slot.Primary]: { name: "Solar Crescents", key: "LMB", description: "Throw three fast darts that bounce through enemies and ankhs.", cooldown: 0 },
    [Slot.Secondary]: { name: "Noon Blade", key: "RMB", description: "Throw a larger bouncing blade and gain bonus health per hit.", cooldown: 6, charges: 2 },
    [Slot.Ability1]: { name: "Dawn Cape", key: "Shift (hold)", description: "Hold while airborne to glide forward.", cooldown: 0 },
    [Slot.Ability2]: { name: "Bright Ankh", key: "E", description: "Fire an ankh that pulls and damages enemies, then redirects your blades.", cooldown: 12, charges: 2 },
    [Slot.Ability3]: { name: "High-Sun Hook", key: "F", description: "Grapple toward the surface under your crosshair.", cooldown: 12 },
    [Slot.Ultimate]: { name: "Sun God Verdict", key: "Q", description: "Aim, then LMB to mark an area; after a delay, talons bombard it for 4.5 seconds.", cooldown: 0 },
  },
  passive: { name: "Zenith Step", description: "Press Space in the air for a rising second leap, once per jump." },
};

function emitProjectile(sim: Parameters<HeroModule["kit"]["tick"]>[0], owner: number, kind: number, from: { x: number; y: number; z: number }, to: { x: number; y: number; z: number }, speed: number, seconds = 1.2) {
  const d = norm(sub(to, from));
  return spawnProjectile(sim, owner, { kind, origin: add(from, scale(d, 0.15)), vel: scale(d, speed), radius: kind === PK.Blade ? 0.2 : 0.12, seconds });
}

function bounceFrom(sim: Parameters<HeroModule["kit"]["tick"]>[0], p: number, owner: number, kind: number, from: { x: number; y: number; z: number }, speed: number) {
  const c = sim.c;
  const b = c.Projectile.bounces[p]!;
  if (b >= 3 || !ownerAlive(sim, owner)) return false;
  const enemy = nearestEnemy(sim, owner, from, 7, { los: false });
  if (enemy && !enemy.construct) {
    const next = emitProjectile(sim, owner, kind, from, add(sim.posOf(enemy.eid), { x: 0, y: sim.hero(enemy.eid).height * 0.55, z: 0 }), speed, 0.7);
    if (next !== undefined) c.Projectile.bounces[next] = b + 1;
    return true;
  }
  const ankh = constructsInRadius(sim, from, 7, (e) => c.Construct.kind[e] === CK.Ankh && c.Construct.owner[e] === owner && e !== p)[0];
  if (ankh) {
    const next = emitProjectile(sim, owner, kind, from, add(sim.posOf(ankh.eid), { x: 0, y: 0.9, z: 0 }), speed, 0.7);
    if (next !== undefined) c.Projectile.bounces[next] = b + 1;
    return true;
  }
  return false;
}

function tickRicochet(sim: Parameters<HeroModule["kit"]["tick"]>[0], p: number, damage: number, speed: number, bonusHealth: boolean) {
  const c = sim.c;
  const owner = c.Projectile.owner[p]!;
  const origin = sim.posOf(p);
  const v = { x: c.Vel.x[p]!, y: c.Vel.y[p]!, z: c.Vel.z[p]! };
  const len = Math.hypot(v.x, v.y, v.z);
  if (len > 0.01) {
    const ankh = rayConstructs(sim, origin, scale(v, 1 / len), len / 60 + 0.2, (e) => c.Construct.kind[e] === CK.Ankh && c.Construct.owner[e] === owner);
    if (ankh && bounceFrom(sim, p, owner, c.Projectile.kind[p]!, add(sim.posOf(ankh.eid), { x: 0, y: 0.9, z: 0 }), speed)) {
      sim.emit({ type: "impact", pos: ankh.pos, kind: FX.Moon });
      sim.destroyEntity(p);
      return;
    }
  }
  const hit = moveProjectile(sim, p);
  if (hit) {
    if (hit.target !== undefined) {
      if (ownerAlive(sim, owner)) {
        applyDamage(sim, hit.target, damage * 0.8 ** (c.Projectile.bounces[p] ?? 0), owner, { crit: hit.head, pos: hit.pos, info: { kind: "projectile", crit: !!hit.head, sourceKind: c.Projectile.kind[p]! } });
        if (bonusHealth) addOverhealth(sim, owner, Math.min(75, c.Health.over[owner]! + 25), secs(6.75), owner);
      }
      if (bounceFrom(sim, p, owner, c.Projectile.kind[p]!, hit.pos, speed)) {
        sim.destroyEntity(p);
        return;
      }
    } else if (hit.construct !== undefined) damageConstruct(sim, hit.construct, damage, owner);
    else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, damage, owner);
    sim.emit({ type: "impact", pos: hit.pos, kind: FX.Dart });
    sim.destroyEntity(p);
    return;
  }
  if (c.Projectile.ticks[p]! <= 0) sim.destroyEntity(p);
}

const module: HeroModule = {
  def,
  kit: {
    init(sim, eid) {
      setHv(sim.c, eid, V.leap, 1);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      if (c.Player.grounded[eid]) setHv(c, eid, V.leap, 1);
      commonInput(sim, eid, input, pressed);
      if (!canAct) return;

      if (useUlt(sim, eid, input, pressed)) {
        spawnZone(sim, eid, ZK.Talons, targetPoint(sim, eid, Slot.Ultimate).pos, 8, 6);
        action(sim, eid, ActionKind.Ult, 24);
      }
      if (pressed & Btn.Ability3 && ready(sim, eid, Slot.Ability3)) {
        const a = aim(sim, eid, 25, false);
        const hit = sim.physics.raycast(a.origin, a.dir, 25);
        if (hit >= 0) {
          consume(sim, eid, Slot.Ability3);
          startMove(sim, eid, MoveKind.Grapple, secs(1.2), 21.5, add(a.origin, scale(a.dir, hit)));
          action(sim, eid, ActionKind.Cast, 9);
        }
      }
      if (pressed & Btn.Jump && !c.Player.grounded[eid] && hv(c, eid, V.leap) > 0) {
        setHv(c, eid, V.leap, 0);
        startMove(sim, eid, MoveKind.Launch, 18, 0, { x: 0, y: 9.5, z: 0 });
        action(sim, eid, ActionKind.Cast, 10);
      }
      // The glide has no cooldown; opening the cape still announces itself like a cast.
      if (pressed & Btn.Ability1 && !c.Player.grounded[eid]) sim.emit({ type: "cast", owner: c.Net.id[eid]!, slot: Slot.Ability1, hero: c.Player.hero[eid]! });
      if (pressed & Btn.Ability2 && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        action(sim, eid, ActionKind.Cast, 15);
        launchProjectile(sim, eid, PK.AnkhShot, 60, input, { radius: 0.18, seconds: 1.2 });
      }
      if (pressed & Btn.Secondary && ready(sim, eid, Slot.Secondary)) {
        consume(sim, eid, Slot.Secondary);
        action(sim, eid, ActionKind.AltFire, 12);
        launchProjectile(sim, eid, PK.Blade, 120, input, { radius: 0.2, seconds: 1.2 });
      }
      if (trigger(sim, eid, !!(input.buttons & Btn.Primary))) {
        c.Weapon.fireCd[eid] = secs(0.57);
        action(sim, eid, ActionKind.Fire, 10);
        const a = aim(sim, eid);
        for (let i = 0; i < 3; i++) {
          const side = (i - 1) * 0.035;
          launchProjectile(sim, eid, PK.Dart, 150, input, { radius: 0.12, seconds: 1.1, dir: norm({ x: a.dir.x + side, y: a.dir.y, z: a.dir.z }) });
        }
      }
    },
    movement(sim, eid, input, mods) {
      if (input.buttons & Btn.Ability1) glide(sim, eid, input, mods, { fallSpeed: 1.5, driftSpeed: 8.7 });
    },
  },
  targets: {
    [Slot.Ultimate]: { range: 45, clearance: 0.25, radius: 8, verb: "Mark" },
  },
  projectiles: {
    [PK.Dart]: {
      visual: { color: "#fff3a6", size: 0.12, trail: true },
      tick(sim, p) {
        tickRicochet(sim, p, 25, 150, false);
      },
    },
    [PK.Blade]: {
      visual: { color: "#ffffff", size: 0.22, trail: true },
      tick(sim, p) {
        tickRicochet(sim, p, 70, 120, true);
      },
    },
    [PK.AnkhShot]: {
      visual: { color: "#fff3a6", size: 0.18, trail: true },
      tick(sim, p) {
        const c = sim.c;
        const owner = c.Projectile.owner[p]!;
        const hit = moveProjectile(sim, p);
        if (hit) {
          if (ownerAlive(sim, owner) && sim.authority) {
            const pos = toGround(sim, hit.pos);
            spawnConstruct(sim, owner, CK.Ankh, pos, c.Look.yaw[owner]!, { seconds: 12 });
            sim.emit({ type: "explosion", pos, radius: 5, kind: FX.Moon });
            for (const { eid: t } of playersInRadius(sim, pos, 5, (e) => isEnemy(sim, owner, e), false)) {
              applyDamage(sim, t, 20, owner, { pos, info: { kind: "explosion", sourceKind: PK.AnkhShot } });
              const p0 = sim.posOf(t);
              c.Ext.x[t] += (pos.x - p0.x) * 2.4;
              c.Ext.z[t] += (pos.z - p0.z) * 2.4;
              knockUp(sim, t, 3);
            }
          }
          sim.destroyEntity(p);
          return;
        }
        if (c.Projectile.ticks[p]! <= 0) sim.destroyEntity(p);
      },
    },
  },
  constructs: {
    [CK.Ankh]: {
      maxHp: 125,
      seconds: 12,
      shape: { kind: "cyl", r: 0.42, h: 1.7 },
      damageable: true,
      blocksShots: false,
      visual: { color: "#fff3a6", shape: "cyl" },
    },
  },
  zones: {
    [ZK.Talons]: {
      visual: { color: "#fff3a6", dome: true },
      tick(sim, z) {
        const age = sim.c.Zone.age[z]!;
        if (age < 90 || (age - 90) % 15 !== 0) return;
        const owner = sim.c.Zone.owner[z]!;
        if (!ownerAlive(sim, owner)) return;
        const pos = add(sim.posOf(z), { x: 0, y: 1, z: 0 });
        sim.emit({ type: "explosion", pos, radius: 5, kind: FX.Talon });
        for (const { eid: t } of playersInRadius(sim, pos, 5, (e) => isEnemy(sim, owner, e), false)) applyDamage(sim, t, 150, owner, { pos, info: { kind: "explosion", sourceKind: ZK.Talons } });
      },
    },
  },
  fx: {
    [FX.Dart]: { color: "#fff3a6" },
    [FX.Moon]: { color: "#ffffff" },
    [FX.Talon]: { color: "#ffe16a" },
  },
};

void dist;
void dot;
void applyStatus;
export default module;
